import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Table from "../../components/Table";
import Button from "../../components/Button";
import FilterSimple from "../../components/FilterSimple";
import Pagination from "../../components/Pagination";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { hasPermission } from "../../services/auth";
import { convertIntToMoney } from "../../helpers/conversions";

import PurchaseInvoiceTabs from "./tabs";
import ModalDfeInvoice from "./ModalDfeInvoice";
import ModalDfeFilter from "./ModalDfeFilter";
import ModalDfeKeys from "./ModalDfeKeys";
import { getDfeStatusLabel, getEmitterLabel } from "./helpers.dfe";

const searchParams = [
    { name: 'Emitente, número ou chave', key: '[number]', operation: 'contain', type: 'text' },
];

const FILTER_KEYS = [
    ['search', 'search'],
    ['status', 'status'],
    ['issuedFrom', 'issued_from'],
    ['issuedTo', 'issued_to'],
];

const normalizePage = page => {
    const parsed = parseInt(page, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const parseFilters = search => {
    const params = new URLSearchParams(search);

    return {
        page: normalizePage(params.get('page')),
        search: params.get('search') || '',
        status: params.get('status') || '',
        issuedFrom: params.get('issued_from') || '',
        issuedTo: params.get('issued_to') || '',
    };
};

const buildQuery = (filters, withPage = true) => {
    const params = new URLSearchParams();

    FILTER_KEYS.forEach(([key, param]) => {
        if (filters[key]) params.set(param, filters[key]);
    });

    if (withPage && filters.page > 1) params.set('page', filters.page);

    return params.toString();
};

/**
 * Caixa de entrada do DF-e (BDM-1038): o que a SEFAZ distribuiu contra o CNPJ
 * do tenant. É um feed para triar — conta de luz, frete e nota emitida por
 * engano chegam aqui junto com as compras —, e só vira nota de entrada quando
 * alguém diz que aquilo é uma compra.
 */
export const DfeInvoiceList = () => {
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });

    const [pending, setPending] = useState(0);
    const [syncedAt, setSyncedAt] = useState(null);
    const [enabled, setEnabled] = useState(true);

    const [selected, setSelected] = useState(null);
    const [showFilter, setShowFilter] = useState(false);
    const [showKeys, setShowKeys] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const filters = useMemo(() => parseFilters(location.search), [location.search]);

    const canManage = hasPermission('manage_purchase_order');

    useEffect(() => {
        getInvoices();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [invoices]);

    const updateUrl = (changes, resetPage = true) => {
        const next = { ...filters, ...changes, page: resetPage ? 1 : (changes.page || filters.page) };
        const search = buildQuery(next);

        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
    };

    const getInvoices = () => {
        setLoading(true);

        api.get(`/dfe-invoice/paginate?${buildQuery(filters)}`).then(res => {
            setInvoices(res.data.resources.data);
            setPending(res.data.pending ?? 0);
            setSyncedAt(res.data.synced_at ?? null);
            setEnabled(res.data.enabled ?? false);
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page);
            setPerPage(res.data.resources.per_page);
            setTotalResults(res.data.resources.total);
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(renderError).then(() => setLoading(false));
    };

    /**
     * Relê o acervo do provedor — quem fala com a SEFAZ é ele, a cada 1h20.
     * Sem período escolhido, os últimos 31 dias (a janela máxima da consulta).
     */
    const sync = () => {
        setSyncing(true);

        const payload = filters.issuedFrom && filters.issuedTo
            ? { from: filters.issuedFrom, to: filters.issuedTo }
            : { from: moment().subtract(31, 'days').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') };

        api.post('/dfe-invoice/sync', payload).then(res => {
            const stored = res.data.stored ?? 0;

            toast.success(stored > 0 ? `${stored} nota(s) nova(s)` : 'Nenhuma nota nova');
            getInvoices();
        }).catch(renderError).then(() => setSyncing(false));
    };

    const generateData = () => {
        const header = ['Emissão', 'Emitente', 'Nota', 'Valor', 'Situação', 'Conteúdo'];

        const rows = invoices.map(invoice => ({
            action: () => setSelected(invoice),
            data: [
                invoice.issued_at ? moment(invoice.issued_at).format('DD/MM/YYYY') : '—',
                getEmitterLabel(invoice),
                invoice.series ? `${invoice.number}/${invoice.series}` : (invoice.number ?? '—'),
                convertIntToMoney(invoice.amount, true),
                <span className={`dfe-status status-${invoice.status}`}>{getDfeStatusLabel(invoice)}</span>,
                invoice.full_xml
                    ? 'Nota completa'
                    : <span className={`dfe-resume`}>Só o resumo</span>,
            ],
        }));

        setData({ header, rows });
    };

    return (
        <>
            <ModalDfeInvoice
                show={selected !== null}
                invoice={selected}
                canManage={canManage}
                close={() => setSelected(null)}
                onChanged={() => { setSelected(null); getInvoices(); }}
            />

            <ModalDfeFilter
                show={showFilter}
                close={() => setShowFilter(false)}
                filters={filters}
                setCustomFilters={updateUrl}
            />

            <ModalDfeKeys
                show={showKeys}
                close={() => setShowKeys(false)}
                onRequested={getInvoices}
            />

            <PanelTemplate id="dfe-invoice-list">
                <PageHeader
                    left={
                        <>
                            <FilterSimple
                                params={searchParams}
                                filter={(value, metadata) => updateUrl({ search: metadata?.value ?? '' })}
                                externalValue={filters.search}
                                externalOption={searchParams[0].key}
                            />
                            <PurchaseInvoiceTabs pending={pending} />
                        </>
                    }
                    right={
                        <>
                            <Button
                                type={filters.status || filters.issuedFrom || filters.issuedTo ? `primary` : `secondary`}
                                size={`small`}
                                svg={`filter`}
                                text={`Filtrar`}
                                action={() => setShowFilter(true)}
                            />
                            {canManage &&
                                <Button
                                    type={`primary`}
                                    size={`small`}
                                    text={syncing ? `Atualizando...` : `Atualizar lista`}
                                    loading={syncing}
                                    action={sync}
                                />
                            }
                        </>
                    }
                />

                {!enabled &&
                    <div className={`dfe-notice`}>
                        <strong>A busca automática está desligada.</strong>
                        <span>
                            Ative em Gestão › Cadastros e ajustes › Fiscal, na empresa emitente. A primeira busca leva até 1h20.
                        </span>
                        <Button
                            type={`secondary`}
                            size={`small`}
                            text={`Ir para a configuração`}
                            action={() => navigate('/fiscal/settings')}
                        />
                    </div>
                }

                {enabled &&
                    <div className={`purchase-invoice-context`}>
                        <span className={`dfe-sync-info`}>
                            {syncedAt
                                ? `Última leitura: ${moment(syncedAt).format('DD/MM/YYYY [às] HH:mm')}`
                                : 'Aguardando a primeira leitura'}
                        </span>
                        {canManage &&
                            <button className={`dfe-keys-link`} onClick={() => setShowKeys(true)}>
                                Buscar por chave
                            </button>
                        }
                    </div>
                }

                <Box className={`no-padding with-pagination`}>
                    <Table header={data.header} rows={data.rows} loading={loading} />
                </Box>

                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={page => updateUrl({ page }, false)}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default DfeInvoiceList;
